import { findPrinterDeviceByName, getDefaultPrinterDevice } from "./devices";
import { BrowserPrintDeviceError, BrowserPrintSendError } from "./errors";
import {
  type BrowserPrintApiOptions,
  normalizeErrorMessage,
  normalizePrinterDevice,
  withTimeout,
} from "./internal";
import type { BrowserPrintPrinterDevice } from "./types";

export interface BrowserPrintSendOptions extends BrowserPrintApiOptions {}

export interface PrintZplOptions extends BrowserPrintSendOptions {
  zpl: string;
  device?: BrowserPrintPrinterDevice;
  deviceName?: string;
}

function bytesForPayload(payload: string): number {
  return new TextEncoder().encode(payload).byteLength;
}

export async function sendRawZpl(
  device: BrowserPrintPrinterDevice,
  zpl: string,
  options: BrowserPrintSendOptions = {},
): Promise<{ bytesSent: number }> {
  const normalizedDevice = normalizePrinterDevice(device, "sendRawZpl(device)");
  const payload = String(zpl);
  const bytesSent = bytesForPayload(payload);

  await withTimeout<void>(
    options.timeoutMs,
    `Timed out while sending ZPL to printer '${normalizedDevice.name ?? "unknown"}'.`,
    (resolve, reject) => {
      try {
        normalizedDevice.send(
          payload,
          () => resolve(),
          (error) =>
            reject(
              new BrowserPrintSendError(
                normalizeErrorMessage(
                  error,
                  "BrowserPrint failed to send ZPL payload.",
                ),
                { cause: error },
              ),
            ),
        );
      } catch (error) {
        reject(
          new BrowserPrintSendError(
            normalizeErrorMessage(
              error,
              "BrowserPrint send call threw unexpectedly.",
            ),
            { cause: error },
          ),
        );
      }
    },
  );

  return { bytesSent };
}

export async function printZpl(
  options: PrintZplOptions,
): Promise<{ deviceName: string; bytesSent: number }> {
  let device = options.device;

  if (device === undefined && options.deviceName !== undefined) {
    device = await findPrinterDeviceByName(options.deviceName, options);
    if (device === undefined) {
      throw new BrowserPrintDeviceError(
        `Printer '${options.deviceName}' was not found.`,
      );
    }
  }

  if (device === undefined) {
    device = await getDefaultPrinterDevice(options);
  }

  const { bytesSent } = await sendRawZpl(device, options.zpl, options);

  return {
    deviceName: device.name ?? options.deviceName ?? "Unknown printer",
    bytesSent,
  };
}
